import { useState, useEffect, useCallback } from 'react'
import { Outlet } from 'react-router-dom'
import { Menu, X, AlertCircle } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Sidebar } from './Sidebar'
import { useDriftToasts, DriftToastList } from '@/components/context/DriftToast'
import { useContextUpdates } from '@/hooks/useContextUpdates'
import { useStartupBootstrap } from '@/hooks/useStartupBootstrap'
import { useUpdatesStream } from '@/hooks/useUpdatesStream'
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback'
import { useAppStore } from '@/stores/appStore'
import { useAuth } from '@/hooks/useAuth'
import { contextService } from '@/services'
import { activeGapCount } from '@/components/fill-gaps/partitionGaps'

const COLLAPSE_BREAKPOINT = 1200
const MOBILE_BREAKPOINT = 768

function useBreakpoint() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : COLLAPSE_BREAKPOINT + 1
  )

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return {
    isMobile: width < MOBILE_BREAKPOINT,
    isCollapsed: width >= MOBILE_BREAKPOINT && width < COLLAPSE_BREAKPOINT,
  }
}

const USE_DEV_SEED = import.meta.env.VITE_USE_DEV_SEED === 'true'

/**
 * Post-auth shell. Owns:
 *   1. The sidebar (responsive: drawer / icon-rail / full)
 *   2. App-level bootstrap — startupService.list() + 401 handler wiring
 *   3. The single shared SSE connection for the active startup
 *   4. Legacy drift toast surface (until Context Explorer pages are removed)
 *
 * Bootstrap state surfaces as a sticky banner — the route still renders
 * underneath so the user can keep working with cached data when a transient
 * 5xx hits. /onboarding routing is handled inside `useStartupBootstrap`.
 */
export function AppShell() {
  const { isMobile, isCollapsed: autoCollapsed } = useBreakpoint()
  const { isAuthenticated } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [manualCollapse, setManualCollapse] = useState<boolean | null>(null)

  const isCollapsed = manualCollapse !== null ? manualCollapse : autoCollapsed
  const handleToggleCollapse = useCallback(() => {
    setManualCollapse((prev) => (prev !== null ? !prev : !autoCollapsed))
  }, [autoCollapsed])

  // ── Bootstrap (calls list() + wires 401 handler) ────────────────────
  const { error: bootstrapError, retry } = useStartupBootstrap()

  // ── Active SSE stream, scoped to the active startup ─────────────────
  const startupId = useAppStore((s) => s.startup?.id ?? null)
  const setOpenGapCount = useAppStore((s) => s.setOpenGapCount)

  // Badge fallback: pages that own gap state (Overview, FillGaps, DomainDetail)
  // already write `openGapCount` after their own fetches. AppShell's badge
  // refetch only matters when the user is on a route that DOESN'T own gap
  // state (Settings, legacy /context surfaces). Debounced so SSE bursts
  // collapse into one fetch.
  const refetchBadge = useCallback(async () => {
    if (!startupId) return
    try {
      // Same definition as Fill Gaps: open + in progress. statusCounts cover
      // every gap regardless of page size, so one row is enough.
      const { statusCounts } = await contextService.getGaps(startupId, { limit: 1 })
      setOpenGapCount(activeGapCount(statusCounts))
    } catch {
      // Badge is best-effort. Failure here just stales the count.
    }
  }, [startupId, setOpenGapCount])
  const debouncedRefetchBadge = useDebouncedCallback(() => {
    void refetchBadge()
  }, 250)
  useUpdatesStream({
    startupId,
    filters: { entityTypes: ['gap'] },
    enabled: !!startupId,
    onUpdate: debouncedRefetchBadge,
  })

  // ── Legacy drift toast plumbing (Context Explorer v1 holdover) ──────
  const { toasts, addToast, dismissToast } = useDriftToasts()
  useContextUpdates({
    enabled: !USE_DEV_SEED && isAuthenticated,
    onDrift: addToast,
  })
  useEffect(() => {
    if (!USE_DEV_SEED) return
    const timer = setTimeout(async () => {
      const { DEV_DRIFT_EVENTS } = await import('@/stores/devSeedData')
      addToast(DEV_DRIFT_EVENTS[0])
    }, 3000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const driftOverlay = (
    <DriftToastList toasts={toasts} onDismiss={dismissToast} />
  )

  if (isMobile) {
    return (
      <div className="flex flex-col h-screen bg-bg-deepest">
        {driftOverlay}
        <header className="flex items-center h-12 px-4 border-b border-border-default bg-bg-primary shrink-0">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="p-1 text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Open navigation"
              >
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-60 bg-bg-primary border-r border-border-default">
              <Sidebar collapsed={false} hideCollapseToggle />
            </SheetContent>
          </Sheet>
          <span className="ml-3 font-display font-semibold text-sm text-text-primary">
            contxtmd
          </span>
        </header>
        {bootstrapError && (
          <BootstrapErrorBanner message={bootstrapError} onRetry={retry} />
        )}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-bg-deepest">
      {driftOverlay}
      <Sidebar collapsed={isCollapsed} onToggleCollapse={handleToggleCollapse} />
      <div className="flex-1 flex flex-col min-w-0">
        {bootstrapError && (
          <BootstrapErrorBanner message={bootstrapError} onRetry={retry} />
        )}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

interface BootstrapErrorBannerProps {
  message: string
  onRetry: () => void
}

function BootstrapErrorBanner({ message, onRetry }: BootstrapErrorBannerProps) {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null
  return (
    <div
      role="alert"
      className="flex items-center gap-3 px-4 py-2 bg-warning/10 border-b border-warning/30 text-sm text-text-primary"
    >
      <AlertCircle size={16} className="text-warning shrink-0" />
      <span className="flex-1">
        We couldn’t load your workspace. <span className="text-text-secondary">{message}</span>
      </span>
      <button
        type="button"
        onClick={onRetry}
        className="px-3 py-1 rounded-md border border-border-default hover:bg-bg-elevated transition-colors text-xs"
      >
        Retry
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="text-text-tertiary hover:text-text-primary transition-colors"
      >
        <X size={14} />
      </button>
    </div>
  )
}
